import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { ClientsTable } from '@/components/revfinops/ClientsTable'
import { ClientFilters } from '@/components/revfinops/ClientFilters'
import { ProtectedRevFinOpsRoute } from '@/components/revfinops/ProtectedRevFinOpsRoute'
import { EditClientModal } from '@/components/revfinops/EditClientModal'
import { DeleteClientModal } from '@/components/revfinops/DeleteClientModal'
import { listClients, RevFinOpsClient } from '@/lib/revfinops-api'
import { useDebounce } from '@/hooks/useDebounce'
import { PlusCircle } from 'lucide-react'

/**
 * ClientsPage - RevFinOps client list and management page
 *
 * Features:
 * - Protected admin-only route
 * - Fetches client list from API on mount
 * - Search and filter by name and status
 * - URL query parameters preserve filter state
 * - Debounced search for performance
 * - Displays clients in sortable table
 * - Loading, error, empty, and no-results states
 * - Create new client button
 * - Retry functionality on error
 *
 * Route: /revfinops/clients
 */
export default function ClientsPage() {
  const navigate = useNavigate()

  const [clients, setClients] = useState<RevFinOpsClient[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // Filter state
  const [searchInput, setSearchInput] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')

  // Edit modal state
  const [editingClient, setEditingClient] = useState<RevFinOpsClient | null>(null)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)

  // Delete modal state
  const [deletingClient, setDeletingClient] = useState<RevFinOpsClient | null>(null)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  // Debounce search for performance
  const debouncedSearch = useDebounce(searchInput, 300)

  /**
   * Load clients from API
   */
  const loadClients = async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await listClients()

      if (response.success && response.data) {
        setClients(response.data)
      } else {
        setError(response.error?.message || 'Failed to load clients')
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Filter clients by search and status
   */
  const filteredClients = useMemo(() => {
    return clients.filter((client) => {
      // Search filter (case-insensitive match on name)
      const matchesSearch =
        debouncedSearch === '' ||
        client.name.toLowerCase().includes(debouncedSearch.toLowerCase())

      // Status filter
      const matchesStatus = statusFilter === 'all' || client.status === statusFilter

      // Both conditions must match (AND logic)
      return matchesSearch && matchesStatus
    })
  }, [clients, debouncedSearch, statusFilter])

  /**
   * Load clients on component mount
   */
  useEffect(() => {
    loadClients()
  }, [])

  // Note: URL search params and navigation state disabled temporarily for TanStack Router migration
  // TODO: Implement using TanStack Router's search params once route definitions are created

  /**
   * Handle retry after error
   */
  const handleRetry = () => {
    loadClients()
  }

  /**
   * Handle create new client
   */
  const handleCreateClient = () => {
    navigate({ to: '/revfinops/clients/new' })
  }

  /**
   * Handle clearing all filters
   */
  const handleClearFilters = () => {
    setSearchInput('')
    setStatusFilter('all')
  }

  /**
   * Handle edit client - opens modal with client data
   */
  const handleEdit = (client: RevFinOpsClient) => {
    setEditingClient(client)
    setIsEditModalOpen(true)
  }

  /**
   * Handle edit success - updates client in list and shows success message
   */
  const handleEditSuccess = (updatedClient: RevFinOpsClient) => {
    // Update client in the list optimistically
    setClients(clients.map(c =>
      c.id === updatedClient.id ? updatedClient : c
    ))

    // Show success notification
    setSuccessMessage('Client updated successfully')

    // Auto-hide success message after 5 seconds
    setTimeout(() => setSuccessMessage(null), 5000)
  }

  /**
   * Handle close modal - clears editing state
   */
  const handleCloseModal = () => {
    setIsEditModalOpen(false)
    setEditingClient(null)
  }

  /**
   * Handle delete client - opens modal with client data
   */
  const handleDelete = (client: RevFinOpsClient) => {
    setDeletingClient(client)
    setIsDeleteModalOpen(true)
  }

  /**
   * Handle delete success - removes client from list and shows success message
   */
  const handleDeleteSuccess = (deletedClientId: string) => {
    // Remove client from the list optimistically
    setClients(clients.filter(c => c.id !== deletedClientId))

    // Show success notification
    setSuccessMessage('Client deleted successfully')

    // Auto-hide success message after 5 seconds
    setTimeout(() => setSuccessMessage(null), 5000)
  }

  /**
   * Handle close delete modal - clears deleting state
   */
  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false)
    setDeletingClient(null)
  }

  return (
    <ProtectedRevFinOpsRoute>
      
        <div className="space-y-6">
          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <h1 className="font-display text-3xl sm:text-4xl font-bold text-white mb-2 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
                Clients
              </h1>
              <p className="text-brand-gray-light font-body text-lg">
                Manage your RevFinOps clients and their information
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={handleCreateClient}
                className="btn-primary flex items-center gap-2"
              >
                <PlusCircle size={20} />
                <span>New Client</span>
              </button>
            </div>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="card border-l-4 border-brand-primary bg-brand-primary/10 animate-fade-in">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-brand-primary"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-brand-primary font-body">
                    {successMessage}
                  </p>
                </div>
                <button
                  onClick={() => setSuccessMessage(null)}
                  className="flex-shrink-0 text-brand-primary/60 hover:text-brand-primary transition-colors"
                  aria-label="Dismiss success message"
                >
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* Filters */}
          {!loading && !error && clients.length > 0 && (
            <ClientFilters
              searchValue={searchInput}
              statusValue={statusFilter}
              onSearchChange={setSearchInput}
              onStatusChange={setStatusFilter}
              resultCount={filteredClients.length}
              totalCount={clients.length}
            />
          )}

          {/* Clients Table */}
          <div className="animate-fade-in-up">
            <ClientsTable
              clients={filteredClients}
              loading={loading}
              error={error}
              onRetry={handleRetry}
              searchValue={searchInput}
              statusValue={statusFilter}
              onClearFilters={handleClearFilters}
              hasActiveFilters={searchInput !== '' || statusFilter !== 'all'}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          </div>
        </div>

        {/* Edit Client Modal */}
        {editingClient && (
          <EditClientModal
            client={editingClient}
            isOpen={isEditModalOpen}
            onClose={handleCloseModal}
            onSuccess={handleEditSuccess}
          />
        )}

        {/* Delete Client Modal */}
        {deletingClient && (
          <DeleteClientModal
            client={deletingClient}
            isOpen={isDeleteModalOpen}
            onClose={handleCloseDeleteModal}
            onSuccess={handleDeleteSuccess}
          />
        )}
      
    </ProtectedRevFinOpsRoute>
  )
}
